import { useEffect } from "react";
import type { Map as MapLibreMap } from "maplibre-gl";
import type { CollabDraft } from "./tacticalCollabRoom";

const SOURCE = "tactical-collab-drafts";
const LINE = "tactical-collab-drafts-line";
const FILL = "tactical-collab-drafts-fill";

type Props = {
  map: MapLibreMap | null;
  drafts: CollabDraft[];
};

function toCollection(drafts: CollabDraft[]): GeoJSON.FeatureCollection {
  const features: GeoJSON.Feature[] = [];
  for (const d of drafts) {
    if (d.kind === "line" && d.coordinates.length >= 2) {
      features.push({
        type: "Feature",
        properties: { color: d.color, role: "line", label: d.label },
        geometry: { type: "LineString", coordinates: d.coordinates },
      });
    } else if (d.kind === "polygon" && d.coordinates.length >= 2) {
      const ring =
        d.coordinates.length >= 3
          ? [...d.coordinates, d.coordinates[0]]
          : d.coordinates;
      features.push({
        type: "Feature",
        properties: { color: d.color, role: d.coordinates.length >= 3 ? "poly" : "line", label: d.label },
        geometry:
          d.coordinates.length >= 3
            ? { type: "Polygon", coordinates: [ring] }
            : { type: "LineString", coordinates: d.coordinates },
      });
    } else if (d.coordinates.length === 1) {
      features.push({
        type: "Feature",
        properties: { color: d.color, role: "point", label: d.label },
        geometry: { type: "Point", coordinates: d.coordinates[0] },
      });
    }
  }
  return { type: "FeatureCollection", features };
}

/** Чужие live-черновики рисования (Y.Doc drafts). */
export function TacticalRemoteDrafts({ map, drafts }: Props) {
  useEffect(() => {
    if (!map) return;
    const ensure = () => {
      if (!map.getSource(SOURCE)) {
        map.addSource(SOURCE, {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
        });
      }
      if (!map.getLayer(LINE)) {
        map.addLayer({
          id: LINE,
          type: "line",
          source: SOURCE,
          filter: ["in", ["get", "role"], ["literal", ["line", "poly"]]],
          paint: {
            "line-color": ["coalesce", ["get", "color"], "#64748b"],
            "line-width": 2,
            "line-dasharray": [2, 2],
            "line-opacity": 0.9,
          },
        });
      }
      if (!map.getLayer(FILL)) {
        map.addLayer({
          id: FILL,
          type: "fill",
          source: SOURCE,
          filter: ["==", ["get", "role"], "poly"],
          paint: {
            "fill-color": ["coalesce", ["get", "color"], "#64748b"],
            "fill-opacity": 0.12,
          },
        });
      }
    };
    const sync = () => {
      try {
        ensure();
        const src = map.getSource(SOURCE) as
          | { setData: (d: GeoJSON.FeatureCollection) => void }
          | undefined;
        src?.setData(toCollection(drafts));
      } catch {
        /* style */
      }
    };
    if (map.isStyleLoaded()) sync();
    else map.once("load", sync);
    map.on("styledata", sync);
    sync();
    return () => {
      map.off("styledata", sync);
      try {
        if (map.getLayer(FILL)) map.removeLayer(FILL);
        if (map.getLayer(LINE)) map.removeLayer(LINE);
        if (map.getSource(SOURCE)) map.removeSource(SOURCE);
      } catch {
        /* disposed */
      }
    };
  }, [map, drafts]);

  return null;
}
